import type { Icon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

/** Cartões de indicadores: rótulo, número e detalhe alinhados entre cartões (subgrid). */
export function KpiGrid({ children, className, testId }: { children: ReactNode; className?: string; testId?: string }) {
  return (
    <div className="overflow-hidden rounded-panel border bg-card" data-testid={testId}>
      <dl className={cn('-mr-px -mb-px m-0 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5', className)}>{children}</dl>
    </div>
  );
}

export function Kpi({ label, value, detail, testId, className }: { label: string; value: ReactNode; detail?: ReactNode; testId?: string; className?: string }) {
  return (
    <div data-testid={testId} className={cn('grid min-w-0 row-span-3 grid-rows-subgrid border-r border-b px-4 py-3.5', className)}>
      <dt className="text-[0.8125rem] leading-tight text-muted-foreground">{label}</dt>
      <dd className="contents">
        <div className="tabular mt-1.5 font-display text-[1.625rem] leading-none font-semibold tracking-tight">{value}</div>
        <div className="mt-1.5 text-xs leading-snug text-muted-foreground">{detail ?? ' '}</div>
      </dd>
    </div>
  );
}

/** Botão só de ícone, com rótulo acessível e tooltip nativo. Alvo de toque de 40px. */
export function IconButton({ icon: IconCmp, label, onClick, disabled, destructive, testId }: { icon: Icon; label: string; onClick?: () => void; disabled?: boolean; destructive?: boolean; testId?: string }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={label}
      title={label}
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      className={cn('size-10 sm:size-9', destructive && 'text-destructive hover:text-destructive')}
    >
      <IconCmp className="size-[1.125rem]" aria-hidden />
    </Button>
  );
}

export function Panel({ title, aside, children, className, id }: { title?: string; aside?: ReactNode; children: ReactNode; className?: string; id?: string }) {
  return (
    <section aria-labelledby={title ? id : undefined} className={cn('min-w-0 rounded-panel border bg-card p-4 sm:p-5', className)}>
      {(title || aside) && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          {title && (
            <h2 id={id} className="font-display text-base font-semibold">
              {title}
            </h2>
          )}
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}

export function PanelSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div role="status" aria-label="Carregando" className="flex flex-col gap-3">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} aria-hidden className="skeleton h-10 w-full" />
      ))}
    </div>
  );
}

export function ErrorNote({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex flex-wrap items-center gap-3 rounded-control bg-destructive-soft px-3 py-2.5 text-sm text-destructive">
      <span>{message}</span>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Tentar de novo
        </Button>
      )}
    </div>
  );
}

export const int = (n: number) => new Intl.NumberFormat('pt-BR').format(n);
export const dec1 = (n: number) => new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1, minimumFractionDigits: 1 }).format(n);
